import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { TGALoader } from "three/examples/jsm/loaders/TGALoader.js";
import { CREATURE_DEFS, type CreatureType, BUILDING_DEFS, type BuildingType, type NatureKey } from "@tribez/shared";

export type { NatureKey };

// EXPLORER stone-age tool/weapon/food props used for dropped-item visuals
// (drop-meshes.ts). Loaded geometry-only via FBX — drop-render recolours them,
// so their dead embedded texture paths don't matter.
export type DropPropKey = "axe" | "club" | "hammer" | "spear" | "shield" | "foodBowl" | "roastedMeat";

// NatureKey is defined in @tribez/shared/doodad-registry (re-exported above)
// so server-side QA tests can import the registry without crossing workspace
// boundaries. Adding a new asset = add a key to NatureKey AND a path here.

export type AssetBundle = {
  vikingMale: GLTF;
  bull: GLTF;
  nature: Record<NatureKey, GLTF>;
  creatures: Record<CreatureType, GLTF>;
  buildings: Partial<Record<BuildingType, THREE.Group>>;
  volcano: THREE.Group | null;  // central map landmark (RedWorks Terrain_Volcano)
  dropProps: Partial<Record<DropPropKey, THREE.Group>>;  // EXPLORER props for dropped-item meshes
};

const DROP_PROP_FILES: Record<DropPropKey, string> = {
  axe:         "/assets/drops/Axe_1A.fbx",
  club:        "/assets/drops/Club_1A.fbx",
  hammer:      "/assets/drops/Hammer_1A.fbx",
  spear:       "/assets/drops/Spear_1A.fbx",
  shield:      "/assets/drops/Shield_1A.fbx",
  foodBowl:    "/assets/drops/Food_Bowl_1A.fbx",
  roastedMeat: "/assets/drops/Roasted_Meat_1A.fbx",
};

const NATURE_FILES: Record<NatureKey, string> = {
  // — existing (Phase 1 coverage) —
  commonTree5:    "/assets/nature/CommonTree_5.gltf",
  pine3:          "/assets/nature/Pine_3.gltf",
  rockMedium3:    "/assets/nature/Rock_Medium_3.gltf",
  pebbleRound3:   "/assets/nature/Pebble_Round_3.gltf",
  bushCommon:     "/assets/nature/Bush_Common.gltf",
  grassTall:      "/assets/nature/Grass_Common_Tall.gltf",
  mushroomCommon: "/assets/nature/Mushroom_Common.gltf",
  // — Phase 2 variety: trees —
  commonTree1:    "/assets/nature/CommonTree_1.gltf",
  commonTree2:    "/assets/nature/CommonTree_2.gltf",
  commonTree3:    "/assets/nature/CommonTree_3.gltf",
  commonTree4:    "/assets/nature/CommonTree_4.gltf",
  pine1:          "/assets/nature/Pine_1.gltf",
  pine2:          "/assets/nature/Pine_2.gltf",
  pine4:          "/assets/nature/Pine_4.gltf",
  pine5:          "/assets/nature/Pine_5.gltf",
  twistedTree1:   "/assets/nature/TwistedTree_1.gltf",
  twistedTree2:   "/assets/nature/TwistedTree_2.gltf",
  twistedTree3:   "/assets/nature/TwistedTree_3.gltf",
  twistedTree4:   "/assets/nature/TwistedTree_4.gltf",
  twistedTree5:   "/assets/nature/TwistedTree_5.gltf",
  deadTree1:      "/assets/nature/DeadTree_1.gltf",
  deadTree2:      "/assets/nature/DeadTree_2.gltf",
  deadTree3:      "/assets/nature/DeadTree_3.gltf",
  deadTree4:      "/assets/nature/DeadTree_4.gltf",
  deadTree5:      "/assets/nature/DeadTree_5.gltf",
  // — Phase 2 variety: rocks —
  rockMedium1:    "/assets/nature/Rock_Medium_1.gltf",
  rockMedium2:    "/assets/nature/Rock_Medium_2.gltf",
  pebbleRound1:   "/assets/nature/Pebble_Round_1.gltf",
  pebbleRound2:   "/assets/nature/Pebble_Round_2.gltf",
  pebbleRound4:   "/assets/nature/Pebble_Round_4.gltf",
  pebbleRound5:   "/assets/nature/Pebble_Round_5.gltf",
  pebbleSquare3:  "/assets/nature/Pebble_Square_3.gltf",
  pebbleSquare4:  "/assets/nature/Pebble_Square_4.gltf",
  // — Phase 2 variety: foliage + flowers —
  bushCommonFlowers: "/assets/nature/Bush_Common_Flowers.gltf",
  fern1:             "/assets/nature/Fern_1.gltf",
  flower3Single:     "/assets/nature/Flower_3_Single.gltf",
  flower3Group:      "/assets/nature/Flower_3_Group.gltf",
  flower4Single:     "/assets/nature/Flower_4_Single.gltf",
  flower4Group:      "/assets/nature/Flower_4_Group.gltf",
  grassCommonShort:  "/assets/nature/Grass_Common_Short.gltf",
  mushroomLaetiporus:"/assets/nature/Mushroom_Laetiporus.gltf",
  // — Stone-age re-theme: EXPLORER baked .glb (foliage alphaMode=MASK patched) —
  exTree1A:  "/assets/nature/Tree_1A.glb",
  exTree1B:  "/assets/nature/Tree_1B.glb",
  exTree1E:  "/assets/nature/Tree_1E.glb",
  exTree8D:  "/assets/nature/Tree_8D.glb",
  exRockM1A: "/assets/nature/Rock_Medium_1A.glb",
  exRockM2A: "/assets/nature/Rock_Medium_2A.glb",
  exRockM3A: "/assets/nature/Rock_Medium_3A.glb",
  exTree2B:     "/assets/nature/Tree_2B.glb",
  exTree2D:     "/assets/nature/Tree_2D.glb",
  exTree2F:     "/assets/nature/Tree_2F.glb",
  exTree3A:     "/assets/nature/Tree_3A.glb",
  exTree3C:     "/assets/nature/Tree_3C.glb",
  exTree3E:     "/assets/nature/Tree_3E.glb",
  exTree4B:     "/assets/nature/Tree_4B.glb",
  exTree4C:     "/assets/nature/Tree_4C.glb",
  exTree5B:     "/assets/nature/Tree_5B.glb",
  exTree5C:     "/assets/nature/Tree_5C.glb",
  exTree6B:     "/assets/nature/Tree_6B.glb",
  exTree6C:     "/assets/nature/Tree_6C.glb",
  exTree7B:     "/assets/nature/Tree_7B.glb",
  exTree7C:     "/assets/nature/Tree_7C.glb",
  exTree8B:     "/assets/nature/Tree_8B.glb",
  exTree8C:     "/assets/nature/Tree_8C.glb",
  exTree8E:     "/assets/nature/Tree_8E.glb",
  exTree5A:     "/assets/nature/Tree_5A.glb",
  exTree7A:     "/assets/nature/Tree_7A.glb",
  exTree8A:     "/assets/nature/Tree_8A.glb",
  exLog1A:      "/assets/nature/Tree_Log_1A.glb",
  exLog2A:      "/assets/nature/Tree_Log_2A.glb",
  exTrunk1A:    "/assets/nature/Tree_Trunk_1A.glb",
  exRockL1A:    "/assets/nature/Rock_Large_1A.glb",
  exRockL2A:    "/assets/nature/Rock_Large_2A.glb",
  exRockL3A:    "/assets/nature/Rock_Large_3A.glb",
  exRockL1B:    "/assets/nature/Rock_Large_1B.glb",
  exRockM1B:    "/assets/nature/Rock_Medium_1B.glb",
  exRockM2B:    "/assets/nature/Rock_Medium_2B.glb",
  exRockM3C:    "/assets/nature/Rock_Medium_3C.glb",
  exRockS1A:    "/assets/nature/Rock_Small_1A.glb",
  exRockS1B:    "/assets/nature/Rock_Small_1B.glb",
  exRockS1C:    "/assets/nature/Rock_Small_1C.glb",
  exRockS1D:    "/assets/nature/Rock_Small_1D.glb",
  exMush1A:     "/assets/nature/Mushroom_1A.glb",
  exMush2A:     "/assets/nature/Mushroom_2A.glb",
  exMush3A:     "/assets/nature/Mushroom_3A.glb",
  exMush4A:     "/assets/nature/Mushroom_4A.glb",
  exMush5A:     "/assets/nature/Mushroom_5A.glb",
  exFern1A:     "/assets/nature/Fern_1A.glb",
  exFern2A:     "/assets/nature/Fern_2A.glb",
  exBone1A:     "/assets/nature/Bone_1A.glb",
  exBone2A:     "/assets/nature/Bone_2A.glb",
  exCarcass1A:  "/assets/nature/Animal_Carcass_1A.glb",
  exCarcass2A:  "/assets/nature/Animal_Carcass_2A.glb",
  exTusk1A:     "/assets/nature/Tusk_1A.glb",
  exCanyonW1A:  "/assets/nature/Canyon_Wall_1A.glb",
  exCanyonW2A:  "/assets/nature/Canyon_Wall_2A.glb",
  exCanyonW3A:  "/assets/nature/Canyon_Wall_3A.glb",
  exCliff1A:    "/assets/nature/Cliff_Formation_1A.glb",
  exCliff2A:    "/assets/nature/Cliff_Formation_2A.glb",
  exCliff5A:    "/assets/nature/Cliff_Formation_5A.glb",
};

// Prehistoric character loader: the RedWorks Caveman is an FBX on a Mixamo
// skeleton, with its animation clips shipped as SEPARATE Mixamo FBX downloads
// (mesh-less, "Without Skin"). FBXLoader gives us a SkinnedMesh + bones; we pull
// clip[0] from each animation FBX and merge them onto the mesh group, renamed to
// the canonical names createPlayer() looks for (Idle/Walk/Attack/Death). Returns
// a GLTF-shaped { scene, animations } — createPlayer only reads those two.
//
// The shared low-poly atlas is assigned explicitly (the FBX's own texture path
// doesn't resolve through Vite); flipY=false is the FBX UV convention (matches
// the proven fbx-viewer recipe). vertexColors preserved if present.
async function loadFbxCharacter(
  meshUrl: string,
  atlasUrl: string,
  clips: { name: string; url: string }[],
): Promise<GLTF> {
  const fbx = new FBXLoader();
  const loadOne = (url: string) =>
    new Promise<THREE.Group>((resolve, reject) => fbx.load(url, resolve, undefined, reject));
  const tex = await new Promise<THREE.Texture>((resolve, reject) =>
    new THREE.TextureLoader().load(atlasUrl, (t) => { t.colorSpace = THREE.SRGBColorSpace; t.flipY = false; resolve(t); }, undefined, reject),
  );
  const [scene, ...clipGroups] = await Promise.all([loadOne(meshUrl), ...clips.map((c) => loadOne(c.url))]);
  scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const hasVColor = !!m.geometry?.getAttribute?.("color");
    const apply = (mat: THREE.Material) => {
      const std = new THREE.MeshStandardMaterial({ map: tex, metalness: 0, roughness: 1, vertexColors: hasVColor, side: THREE.DoubleSide });
      std.name = (mat as THREE.Material & { name?: string }).name ?? "";
      return std;
    };
    m.material = Array.isArray(m.material) ? m.material.map(apply) : apply(m.material);
  });
  const animations = clipGroups.map((g, i) => {
    const clip = g.animations[0];
    if (clip) clip.name = clips[i]!.name;
    return clip;
  }).filter(Boolean) as THREE.AnimationClip[];
  return { scene, animations } as unknown as GLTF;
}

export async function preloadAssets(): Promise<AssetBundle> {
  const loader = new GLTFLoader();
  const load = (url: string) =>
    new Promise<GLTF>((resolve, reject) => loader.load(url, resolve, undefined, reject));

  const natureKeys = Object.keys(NATURE_FILES) as NatureKey[];
  // Single source of truth: iterate the registry, not a parallel mesh-path table.
  // Skip creatures with a null meshUrl (e.g. orb-rendered Nightwisp): they have
  // no GLTF, render procedurally at draw time, and loading the missing path
  // would 404 → SPA-fallback HTML → reject this whole preload (breaking world
  // load). Same null-skip pattern as buildings below.
  const creatureKeys = (Object.keys(CREATURE_DEFS) as CreatureType[]).filter(
    (k) => CREATURE_DEFS[k].meshUrl != null,
  );
  // Player avatar: prehistoric Caveman (FBX + Mixamo clips). Replaces the
  // Quaternius Viking placeholder. Kept under the `vikingMale` bundle key to
  // avoid threading a rename through every call site.
  const caveman = loadFbxCharacter(
    "/assets/characters/Caveman.fbx",
    "/assets/characters/RedWorksLowPolyTexture_00.jpg",
    [
      { name: "Idle",   url: "/assets/characters/Caveman_Idle.fbx" },
      { name: "Walk",   url: "/assets/characters/Caveman_Walk.fbx" },
      { name: "Run",    url: "/assets/characters/Caveman_Run.fbx" },
      { name: "Attack", url: "/assets/characters/Caveman_Attack.fbx" },
      { name: "Death",  url: "/assets/characters/Caveman_Death.fbx" },
    ],
  );
  const [vikingMale, bull, ...rest] = await Promise.all([
    caveman,
    load("/assets/animals/Bull.gltf"),
    ...natureKeys.map((k) => load(NATURE_FILES[k])),
    ...creatureKeys.map((k) => load(CREATURE_DEFS[k].meshUrl!)),
  ]);

  const natureGltfs = rest.slice(0, natureKeys.length);
  const creatureGltfs = rest.slice(natureKeys.length);

  const nature = {} as Record<NatureKey, GLTF>;
  natureKeys.forEach((k, i) => { nature[k] = natureGltfs[i]!; });

  // TwistedTree foliage thinning. The Quaternius `Leaves_TwistedTree` material
  // is the dense red blob canopy used by AiurTree (257 instances) +
  // BelShirTree (43) — both KTS alien biomes that cluster in map corners.
  // CommonTree (green) and Pine (dark green) leaves stay untouched.
  // Tunable from this single block. Current setting is intentionally extreme
  // (mostly-cutout) to validate the see-through effect; dial back from here.
  const RED_LEAF_OPACITY = 0.15;
  const RED_LEAF_ALPHA_TEST = 0.5;
  for (const gltf of natureGltfs) {
    gltf.scene.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of mats) {
        if (!mat || !mat.name || !/^Leaves_TwistedTree/i.test(mat.name)) continue;
        const std = mat as THREE.MeshStandardMaterial;
        std.transparent = true;
        std.opacity = RED_LEAF_OPACITY;
        std.alphaTest = RED_LEAF_ALPHA_TEST;
        std.depthWrite = false;
        std.side = THREE.DoubleSide;
      }
    });
  }

  const creatures = {} as Record<CreatureType, GLTF>;
  creatureKeys.forEach((k, i) => { creatures[k] = creatureGltfs[i]!; });

  // Buildings: same single-source-of-truth pattern as creatures. Skip rows whose
  // meshUrl is null (none currently — all 12 buildings ship FBX as of the Phase 2
  // mesh pass; the null path stays as a safety fallback for future buildings).
  // A single failed FBX load is logged and falls through to the primitive
  // fallback rather than breaking the entire preload.
  // TGA-aware FBX loader: the stone-age building meshes (EXPLORER + RedWorks)
  // reference their textures by the original .tif/.psd authoring path, which
  // doesn't resolve through Vite — so the textures are assigned EXPLICITLY below
  // via BUILDING_TEX. The manager's .tga handler covers EXPLORER's TGA diffuse maps.
  const bldManager = new THREE.LoadingManager();
  bldManager.addHandler(/\.tga$/i, new TGALoader(bldManager));
  const fbxLoader = new FBXLoader(bldManager);
  const loadFbx = (url: string) =>
    new Promise<THREE.Group>((resolve, reject) => fbxLoader.load(url, resolve, undefined, reject));

  // Per-building diffuse texture (filename under /assets/buildings/). The reskinned
  // meshes carry no resolvable embedded texture, so we map + assign explicitly.
  // Locked mapping: docs/build/building-mesh-mapping.md.
  const RW_ATLAS = "RedWorksLowPolyTexture_00.jpg";
  const BUILDING_TEX: Partial<Record<BuildingType, string>> = {
    Tent: "Tent_1A_D.png",
    Campfire: RW_ATLAS,
    Palisade: RW_ATLAS,
    Forge: "Props_1A_D.png",
    Workbench: "Props_1A_D.png",
    HuntersLodge: "Hut_2A_D.png",
    StorageOutpost: "Stockpile_1A_D.png",
    Tavern: RW_ATLAS,
    Farm: "Props_1A_D.png",
    AutomatedDefenseTurret: "Tower_1A_D.png",
    SpikeTrap: "Buildings_1A_D.png",
    Wonder: "Rocks_1A_D.png",
  };
  // Palette tune: some EXPLORER atlases read too red/dark under our lighting (esp.
  // the Rocks atlas on the Wonder totem). Desaturate toward luminance + brighten at
  // load time via a canvas pass. {desat:0..1 toward grey, bright:multiplier}. PNG only
  // (TGA decodes to a DataTexture without an HTMLImageElement to canvas-draw).
  // The EXPLORER atlases ship dark + saturated (read too red/dim under our Lambert
  // lighting); brighten + partly desaturate them so buildings "accept light" and
  // sit beside the RedWorks-atlas buildings (which already read fine and are left
  // untouched). Rocks atlas (Wonder) gets the strongest lift.
  const TEX_ADJUST: Record<string, { desat: number; bright: number }> = {
    "Rocks_1A_D.png":     { desat: 0.55, bright: 1.20 },
    "Buildings_1A_D.png": { desat: 0.45, bright: 1.30 },
    "Props_1A_D.png":     { desat: 0.30, bright: 1.25 },
    "Tower_1A_D.png":     { desat: 0.35, bright: 1.28 },
    "Tent_1A_D.png":      { desat: 0.20, bright: 1.28 },
    "Hut_2A_D.png":       { desat: 0.20, bright: 1.25 },
    "Stockpile_1A_D.png": { desat: 0.20, bright: 1.22 },
  };
  const adjustImage = (img: TexImageSource & { width: number; height: number }, desat: number, bright: number): THREE.CanvasTexture => {
    const c = document.createElement("canvas");
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext("2d")!;
    ctx.drawImage(img as CanvasImageSource, 0, 0);
    const d = ctx.getImageData(0, 0, c.width, c.height);
    const a = d.data;
    for (let i = 0; i < a.length; i += 4) {
      const r = a[i]!, g = a[i + 1]!, b = a[i + 2]!;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      a[i]     = Math.min(255, (r + (lum - r) * desat) * bright);
      a[i + 1] = Math.min(255, (g + (lum - g) * desat) * bright);
      a[i + 2] = Math.min(255, (b + (lum - b) * desat) * bright);
    }
    ctx.putImageData(d, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace; t.flipY = false;
    return t;
  };
  const texCache = new Map<string, Promise<THREE.Texture>>();
  const loadBldTex = (file: string) => {
    let p = texCache.get(file);
    if (!p) {
      const isTga = /\.tga$/i.test(file);
      const ldr = isTga ? new TGALoader() : new THREE.TextureLoader();
      const adjust = !isTga ? TEX_ADJUST[file] : undefined;
      p = new Promise<THREE.Texture>((resolve, reject) =>
        ldr.load(`/assets/buildings/${file}`, (t) => {
          if (adjust && (t as THREE.Texture).image) {
            resolve(adjustImage((t as THREE.Texture).image, adjust.desat, adjust.bright));
          } else {
            t.colorSpace = THREE.SRGBColorSpace; t.flipY = false; resolve(t);
          }
        }, undefined, reject));
      texCache.set(file, p);
    }
    return p;
  };
  const applyBuildingTexture = async (group: THREE.Group, k: BuildingType): Promise<THREE.Group> => {
    const file = BUILDING_TEX[k];
    if (!file) return group;  // keep native materials (legacy Quaternius buildings)
    const tex = await loadBldTex(file);
    group.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh) return;
      const apply = (src: THREE.Material) => {
        // FrontSide: buildings are solid closed meshes. DoubleSide renders backfaces
        // with flipped normals, which the Fresnel rim (added by toLambertWithRim) then
        // washes white across large flat angled surfaces (tent canvas, roofs).
        const std = new THREE.MeshStandardMaterial({ map: tex, metalness: 0, roughness: 1, side: THREE.FrontSide });
        std.name = (src as THREE.Material & { name?: string })?.name ?? "";
        return std;
      };
      m.material = Array.isArray(m.material) ? m.material.map(apply) : apply(m.material);
    });
    return group;
  };

  // Quaternius / Synty FBX exports ship MeshPhongMaterial arrays with opacity=0
  // and transparent=true on every submesh. Three's FBXLoader honors those
  // values verbatim, which renders the entire group invisible. Scrub them at
  // load-time so downstream code (clones, reconcile loop) works with
  // sane defaults. Applies to all materials, including arrays.
  const fixFbxMaterials = (group: THREE.Group): THREE.Group => {
    group.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of mats) {
        if (!mat) continue;
        if ("opacity" in mat) (mat as THREE.MeshPhongMaterial).opacity = 1;
        if ("transparent" in mat) (mat as THREE.MeshPhongMaterial).transparent = false;
      }
    });
    return group;
  };

  const buildingKeys = Object.keys(BUILDING_DEFS) as BuildingType[];
  const buildings: Partial<Record<BuildingType, THREE.Group>> = {};
  await Promise.all(buildingKeys.map(async (k) => {
    const meshUrl = BUILDING_DEFS[k].meshUrl;
    if (!meshUrl) return;  // primitive fallback used at render time
    try {
      buildings[k] = await applyBuildingTexture(fixFbxMaterials(await loadFbx(meshUrl)), k);
    } catch (err) {
      console.warn(`[assets] FBX load failed for ${k}: ${(err as Error).message}`);
    }
  }));

  // Volcano landmark — single fused RedWorks Terrain_Volcano mesh (200×72×200:
  // a 200u flat tile with a central cone). Loaded textured here; world.ts scales
  // it down + sinks the flat tile below terrain so only the cone shows.
  let volcano: THREE.Group | null = null;
  try {
    const v = fixFbxMaterials(await loadFbx("/assets/landmark/Volcano.fbx"));
    const vtex = await loadBldTex(RW_ATLAS);
    v.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh) return;
      const apply = () => { const s = new THREE.MeshStandardMaterial({ map: vtex, metalness: 0, roughness: 1, side: THREE.DoubleSide }); return s; };
      m.material = Array.isArray(m.material) ? m.material.map(apply) : apply();
    });
    volcano = v;
  } catch (err) {
    console.warn(`[assets] volcano load failed: ${(err as Error).message}`);
  }

  // Drop props (EXPLORER tool/weapon/food meshes). Geometry-only: drop-meshes
  // recolours them with a toon tint, so the FBX's unresolved texture refs are
  // harmless (a failed prop just falls back to a nature prop at render time).
  const dropProps: Partial<Record<DropPropKey, THREE.Group>> = {};
  await Promise.all((Object.keys(DROP_PROP_FILES) as DropPropKey[]).map(async (k) => {
    try { dropProps[k] = fixFbxMaterials(await loadFbx(DROP_PROP_FILES[k])); }
    catch (err) { console.warn(`[assets] drop prop ${k} load failed: ${(err as Error).message}`); }
  }));

  return { vikingMale, bull, nature, creatures, buildings, volcano, dropProps };
}
